import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

// Hooks
import { useStore } from "~/hooks/useStore";
// Components
import CurrentChosenHospital from "~/components/common/CurrentChosenHospital/CurrentChosenHospital";
import AdditionalHospitals from "~/components/common/AdditionalHospitals/AdditionalHospitals";
import SpecialityCard from "~/components/cards/speciality/SpecialityCard";
import NoFountEntity from "~/components/common/NoFoundEntity/NoFountEntity";
// Styles
import s from "~/views/appointment/speciality/SpecialityPage.module.scss";
import styles from "~/App.module.scss";
// Types
import { ISpecialityEntity } from "~/types/specialties";
// Utils
import { goToHelpPage } from "~/utils/helpers";
import ROUTES from "~/utils/routes";
import { logEvent } from "~/utils/amplitude";

interface Props {
  hospitalId?: number | null;
  previousHospitalId: number | null | undefined;
}

const SpecialityPage: React.FC<Props> = observer(
  ({ hospitalId, previousHospitalId }) => {
    const store = useStore();
    const specialities: ISpecialityEntity[] =
      store.specialityStore.specialities;
    const hospitals = store.hospitalStore.additionalHospitals;
    const navigate = useNavigate();

    useEffect(() => {
      logEvent("Э/ Страница с списком специальностей", {});
    }, []);

    const changeHospital = (id: number) => {
      store.hospitalStore.setCurrentHospital(id);
      navigate(ROUTES.SPECIALTIES.PATHNAME, { replace: true });
    };

    useEffect(() => {
      if (
        store.cityStore.currentCity?.area_code &&
        store.patientStore.currentPatient?.idUserData
      ) {
        if (
          previousHospitalId !== hospitalId ||
          store.cityStore.isMoscowRegion
        ) {
          store.specialityStore.fetchSpecialities({
            id_region: store.cityStore.currentCity.area_code,
            id_user_data: store.patientStore.currentPatient.idUserData,
            ...(hospitalId && { id_hospital: hospitalId }),
          });
        }
      } else {
        navigate(ROUTES.HOSPITALS.PATHNAME, { replace: true });
      }
    }, [
      store.specialityStore,
      store.hospitalStore.currentHospital,
      store.patientStore.currentPatient,
      store.cityStore.currentCity,
      store.cityStore.isMoscowRegion,
      previousHospitalId,
      hospitalId,
      navigate,
    ]);

    const handleClickSpecialityCard = (speciality: ISpecialityEntity): void => {
      store.specialityStore.setCurrentSpeciality(speciality.idSpecialty);

      logEvent("Д/ Нажал плашку специальности", {
        "Название специальности": speciality.name,
      });

      store.cityStore.isMoscowRegion
        ? store.appointmentStepsStore.setCurrentStepForDisabling(0)
        : store.appointmentStepsStore.setCurrentStepForDisabling(1);

      navigate(ROUTES.DOCTORS.PATHNAME);
    };

    return (
      <div className="screen-container">
        {!store.cityStore.isMoscowRegion && (
          <div className={s.layoutCurrentHospital}>
            <CurrentChosenHospital />
          </div>
        )}

        {!!(!store.systemStore.loading && specialities.length) && (
          <>
            <div className={styles.appointmentHeadingWrap}>
              <h2 className="headline-2">
                Специальности доступные для записи:
              </h2>
            </div>

            <div className={s.layoutChooseSection}>
              <section className={s.specialityCardsGrid}>
                {specialities.map((speciality) => (
                  <SpecialityCard
                    key={speciality.idSpecialty}
                    title={speciality.name}
                    onClick={() => handleClickSpecialityCard(speciality)}
                  />
                ))}
              </section>
            </div>
          </>
        )}

        {!!(!store.systemStore.loading && !specialities.length) && (
          <div className={s.hospitalNoFoundData}>
            <NoFountEntity
              title="Нет подходящих специальностей"
              subtitle="Не нашли специальность? Расскажите нам об этом!"
              buttonText="Не нашли специальность"
              onClick={goToHelpPage}
            />
          </div>
        )}

        {!store.cityStore.isMoscowRegion && (
          <AdditionalHospitals hospitals={hospitals} onClick={changeHospital} />
        )}
      </div>
    );
  }
);

export default SpecialityPage;
